import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import bb from 'billboard.js/dist/billboard.pkgd.min.js';
import {useDispatch, useSelector} from 'react-redux';

import AllDataIcon from '@/assets/icons/AllData.svg';
import ZoomInIcon from '@/assets/icons/ZoomIn.svg';
import ZoomOutIcon from '@/assets/icons/ZoomOut.svg';
import {getAutoProfileGraph, initAutoProfileGraph} from '@/businesses/autoProfileGraphBusiness.js';
import Button from '@/components/button/Button';
import * as APPDATA from '@/constants/pcConstAppData';
import useHub from '@/hooks/useHub.js';
import useInterval from '@/hooks/useInterval.js';
import useIntlFormat from '@/hooks/useIntlFormat.js';

import AutoProfilePageMenu from '../autoProfile/reusable/AutoProfilePageMenu.jsx';
import AutoProfileTopMenu from '../autoProfile/reusable/AutoProfileTopMenu.jsx';

import {getInitVolumeSensor, volumeSensorZoom} from './data/volumeSensorDataSource.js';
import LoadingVolumeSensorGraph from './LoadingVolumeSensorGraph.jsx';
import {ChartZoomLevel, ID_CHART} from './volumeSensorGraph';

import 'billboard.js/dist/billboard.min.css';

import './VolumeSensorChart.less';

function VolumeSensorGraph() {
  const {formatMessage} = useIntlFormat();
  const dispatch = useDispatch();
  const {on, off} = useHub();

  const runningMethod = useSelector((state) => state.autoProfileState.runningMethod);
  const estimateTime = useSelector((store) => store.setupStore.estimateTime);
  const highVolumeBuffer = useSelector(
    (state) => state.setupStore.currentAdvSetupParams.ConfigAdvSetup.GeneralAdvSetup.HighVolumeBufferFlag,
  );
  const isRunning = useSelector(
    (store) =>
      ![
        APPDATA.DEVICE_STATE.NONE,
        APPDATA.DEVICE_STATE.IDLE,
        APPDATA.DEVICE_STATE.STOPPING,
        APPDATA.DEVICE_STATE.PAUSED,
      ].includes(store.setupStore.currentPCState),
  );

  const [avgConcentrationTime, setAvgConcentrationTime] = useState(-1);
  const [elapsedTime, setElapsedTime] = useState(-1);

  const [zoomLevel, setZoomLevel] = useState(ChartZoomLevel.MINIMUM_ZOOM_LEVEL);

  const [isLoading, setLoading] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(false);

  const chartRef = useRef(null);

  const handleChartInterval = useCallback(async () => {
    const {
      avgConcentarionRate: _avgConcentarionRate = -1,
      elapsedTime: _elapsedTime = -1,
      rowValue: __rowValue,
    } = await dispatch(getAutoProfileGraph());

    setAvgConcentrationTime(_avgConcentarionRate);
    setElapsedTime(_elapsedTime);
    setLoading(false);
    setIsDataAvailable(true);

    chartRef.current.load({
      rows: [getInitVolumeSensor(runningMethod, highVolumeBuffer), ...volumeSensorZoom(__rowValue, zoomLevel)],
    });
  }, [dispatch, runningMethod, zoomLevel]);

  const {start, stop} = useInterval(handleChartInterval);

  useEffect(() => {
    if (isRunning && isDataAvailable) {
      start();
    } else {
      stop();
    }

    return () => {
      stop();
      // if (chartRef.current) chartRef.current.destroy();
    };
  }, [isDataAvailable, isRunning, off, on, start, stop]);

  // #endregion
  useEffect(() => {
    async function init() {
      await setLoading(true);

      const {
        avgConcentarionRate: _avgConcentarionRate = -1,
        elapsedTime: _elapsedTime = -1,
        rowValue: __rowValue,
      } = await dispatch(initAutoProfileGraph());

      setAvgConcentrationTime(_avgConcentarionRate);
      setElapsedTime(_elapsedTime);
      setLoading(false);

      if (__rowValue.data.rows.length > 1) {
        setIsDataAvailable(true);
        chartRef.current = bb.generate(__rowValue);
      }
    }

    init();
  }, [dispatch, off, on]);

  const handleZoomClick = async (e) => {
    const {id} = e.currentTarget;

    let val = ChartZoomLevel.MINIMUM_ZOOM_LEVEL;
    switch (id) {
      case 'zoomOut': {
        val = zoomLevel <= ChartZoomLevel.MINIMUM_ZOOM_LEVEL ? ChartZoomLevel.MINIMUM_ZOOM_LEVEL : zoomLevel - 1;
        break;
      }
      case 'zoomIn': {
        val = zoomLevel >= ChartZoomLevel.MAXIMUM_ZOOM_LEVEL ? ChartZoomLevel.MAXIMUM_ZOOM_LEVEL : zoomLevel + 1;
        break;
      }
      case 'resetChart':
        val = ChartZoomLevel.MINIMUM_ZOOM_LEVEL;
        break;
      default:
        break;
    }

    setZoomLevel(val);

    const {
      avgConcentarionRate: _avgConcentarionRate = -1,
      elapsedTime: _elapsedTime = -1,
      rowValue: __rowValue,
    } = await dispatch(getAutoProfileGraph());

    setAvgConcentrationTime(_avgConcentarionRate);
    setElapsedTime(_elapsedTime);
    setLoading(false);
    setIsDataAvailable(true);

    chartRef?.current.load({
      rows: [getInitVolumeSensor(runningMethod, highVolumeBuffer), ...volumeSensorZoom(__rowValue, val)],
    });
  };

  const loadingText = useMemo(() => {
    let result = null;

    if (!isDataAvailable) result = formatMessage({id: 'volumeSensorChart.label.emptyState'});

    if (isLoading || (!isDataAvailable && isRunning)) result = formatMessage({id: 'volumeSensorChart.label.loading'});

    return result;
  }, [isDataAvailable, formatMessage, isLoading, isRunning]);

  return (
    <div>
      <div className="row">
        <div className="col-md-8">
          {loadingText ? (
            <LoadingVolumeSensorGraph text={loadingText} />
          ) : (
            <div
              className="chartElement"
              id={ID_CHART.replace('#', '')}
            />
          )}
        </div>
        <div className="col-md-4 no-padding">
          <div className="row volSensorContentRow">
            <span>{formatMessage({id: 'volumeSensorChart.label.avgConcRate'})}</span>
            <br />
            <input
              className="volumeProps"
              value={avgConcentrationTime === -1 || isLoading ? 'Loading...' : avgConcentrationTime}
              disabled
            />{' '}
            <span>{formatMessage({id: 'general.unit.mlPerMin'})}</span>
          </div>
          <div className="row volSensorContentRow">
            <span>{formatMessage({id: 'volumeSensorChart.label.elapsedTime'})}</span>
            <br />
            <input
              className="volumeProps"
              value={elapsedTime === -1 || isLoading ? 'Loading...' : elapsedTime}
              disabled
            />
          </div>
          <div className="row volSensorContentRow">
            <span>{formatMessage({id: 'volumeSensorChart.label.estRemainingTime'})}</span>
            <br />
            <input
              className="volumeProps"
              value={estimateTime === -1 || isLoading ? 'Loading...' : estimateTime}
              disabled
            />
          </div>
          <div className="row center">
            <Button
              id="zoomIn"
              onClick={handleZoomClick}
              className="volChartActionButton"
              icon={ZoomInIcon}
              disabled={zoomLevel === ChartZoomLevel.MAXIMUM_ZOOM_LEVEL || !isDataAvailable}
            />
            <Button
              id="zoomOut"
              onClick={handleZoomClick}
              className="volChartActionButton"
              icon={ZoomOutIcon}
              disabled={zoomLevel === ChartZoomLevel.MINIMUM_ZOOM_LEVEL}
            />
            <Button
              id="resetChart"
              onClick={handleZoomClick}
              className="volChartActionButton"
              icon={AllDataIcon}
              disabled={zoomLevel === ChartZoomLevel.MINIMUM_ZOOM_LEVEL}
            />
          </div>
          <div className="row rowOfChartLegend">
            <ul id="legend" />
          </div>
        </div>
      </div>
      <AutoProfileTopMenu
        activeTopMenu={APPDATA.VOLUME_SENSOR_CHART}
        titleTopMenu={APPDATA.AUTO_PROFILE.toUpperCase()}
      />
      <AutoProfilePageMenu />
    </div>
  );
}

export default memo(VolumeSensorGraph);
